import React, { useState } from 'react';
import { useLoading } from '../../../contexts/LoadingContext';
import ErrorMessage from '../../common/ErrorMessage';
import LoadingButton from '../../common/LoadingButton';
import GoogleSignInButton from '../GoogleSignInButton';

const EmailStep = ({ 
    onSubmit, 
    initialData = {}, 
    onNavigateToLogin = () => {}, 
    onNavigateToSignup = () => {},
    handleLoginSuccess = () => {} 
}) => {
    const [formData, setFormData] = useState({
        email: initialData.email || '',
        agreedToTerms: false
    });
    const [error, setError] = useState('');
    const { startLoading, stopLoading } = useLoading();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!formData.agreedToTerms) {
            setError('Please agree to the Terms and Privacy Policy');
            return;
        }

        startLoading('email');
        
        try {
            await onSubmit({
                ...formData,
            }, setError);
        } catch (error) {
            console.error('Form submission error:', error);
            if (error.response?.data) {
                const errorData = error.response.data;
                console.log('Error response data:', errorData);
                
                if (errorData.email) {
                    setError(errorData.email[0]);
                } else if (errorData.non_field_errors) {
                    setError(errorData.non_field_errors[0]);
                } else if (errorData.detail) {
                    setError(errorData.detail);
                } else if (typeof errorData === 'string') {
                    setError(errorData);
                } else {
                    const firstError = Object.values(errorData)[0];
                    setError(Array.isArray(firstError) ? firstError[0] : firstError);
                }
            } else if (error.message) {
                setError(error.message);
            } else {
                setError('An error occurred during submission. Please try again.');
            }
        } finally {
            stopLoading('email');
        }
    };

    return (
        <div className="space-y-8 bg-transparent">
            <div className="text-center">
                <h1>Create Account</h1>
                <p className="mt-4">
                    Already have an account?{' '}
                    <button
                        onClick={onNavigateToLogin}
                        className="font-normal text-popashot-blue-light hover:text-popashot-blue border-none bg-transparent"
                    >
                        Sign in
                    </button>
                </p>
            </div>
            <form className="space-y-6" onSubmit={handleSubmit}>
                <GoogleSignInButton />

                <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-300" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-gray-50 text-gray-500">
                            or
                        </span>
                    </div>
                </div>

                <div className="rounded-md shadow-sm">
                    <div>
                        <label htmlFor="email" className="sr-only">
                            Email address
                        </label>
                        <input
                            id="email"
                            name="email"
                            type="email"
                            required
                            className="appearance-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                            placeholder="Email address"
                            autoComplete="email"
                            spellCheck="false"
                            enterKeyHint="next"
                            aria-label="Email address"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        />
                    </div>
                </div>

                <div className="flex items-start mb-4">
                    <div className="flex items-center h-6">
                        <input
                            id="terms"
                            name="terms"
                            type="checkbox"
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            checked={formData.agreedToTerms}
                            onChange={(e) => setFormData({ ...formData, agreedToTerms: e.target.checked })}
                        />
                    </div>
                    <div className="ml-3 text-sm">
                        <label htmlFor="terms" className="font-medium text-gray-700">
                            I agree to the{' '}
                            <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-popashot-blue-light hover:text-popashot-blue">
                                Terms & Conditions
                            </a>{' '}
                            and{' '}
                            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-popashot-blue-light hover:text-popashot-blue">
                                Privacy Policy
                            </a>
                        </label>
                    </div>
                </div>

                <ErrorMessage error={error} />

                <LoadingButton
                    type="submit"
                    className="btn-primary"
                    loadingKey="email"
                >
                    Continue
                </LoadingButton>
            </form>
        </div>
    );
};

export default EmailStep;
